import { ScriptLoader } from '~/shared/utils/script-loader'
import type { VKVideoPlayerConstructor, VKVideoPlayerEvent, VKVideoPlayerState } from '~/shared/types/vk-video-player'

const VK_VIDEO_PLAYER_SCRIPT_URL = 'https://vk.com/js/api/videoplayer.js'
const VK_VIDEO_END_THRESHOLD_SECONDS = 1

interface CreateVkVideoPlayerOptions {
  iframe: HTMLIFrameElement
  onStarted?: () => void
  onEnded?: () => void
}

export interface VkVideoPlayerController {
  destroy: () => void
}

const getVkVideoPlayer = (): VKVideoPlayerConstructor | null => {
  if (!import.meta.client) {
    return null
  }

  return window.VK?.VideoPlayer ?? null
}

export const getVkVideoUrl = (url: string): string => {
  if (!url || url.includes('js_api=1')) {
    return url
  }

  return `${url}${url.includes('?') ? '&' : '?'}js_api=1`
}

export const createVkVideoPlayer = async ({
  iframe,
  onStarted,
  onEnded,
}: CreateVkVideoPlayerOptions): Promise<VkVideoPlayerController | null> => {
  if (!import.meta.client || !iframe.src) {
    return null
  }

  await ScriptLoader.attach(VK_VIDEO_PLAYER_SCRIPT_URL)

  const videoPlayerApi = getVkVideoPlayer()
  if (!videoPlayerApi) {
    return null
  }

  const player = videoPlayerApi(iframe)
  let isCompleted = false

  const handleCompleted = () => {
    if (isCompleted) {
      return
    }

    isCompleted = true
    onEnded?.()
  }

  const handleTimeUpdate = (state?: VKVideoPlayerState) => {
    if (!state?.duration) {
      return
    }

    if (state.time >= state.duration - VK_VIDEO_END_THRESHOLD_SECONDS) {
      handleCompleted()
    }
  }

  const listeners: Array<[VKVideoPlayerEvent, (state?: VKVideoPlayerState) => void]> = [
    [videoPlayerApi.Events.STARTED, () => onStarted?.()],
    [videoPlayerApi.Events.ENDED, handleCompleted],
    [videoPlayerApi.Events.TIMEUPDATE, handleTimeUpdate],
  ]

  listeners.forEach(([event, listener]) => player.on(event, listener))

  return {
    destroy: () => {
      listeners.forEach(([event, listener]) => player.off(event, listener))
      player.destroy()
    },
  }
}
